/**
 * A file's rows for the data workbench — the same hooks serve the intake file
 * page and the audit's file page; only the base path differs.
 */
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import * as rowsApi from '../api/FileRows';
import { followUrl } from '../api/download';
import { parsed } from '../api/parse';
import {
	fileRowsExportSchema,
	fileRowsPageSchema,
	fileRowsSummarySchema,
	fileRowValuesSchema,
	type FileRowFilters,
	type FileRowsQuery,
} from '../schemas';
import { keys } from './keys';

export function useFileRowsSummaryQuery(base: string, enabled: boolean) {
	return useQuery({
		queryKey: keys.fileRows.summary(base),
		queryFn: async () =>
			parsed(
				fileRowsSummarySchema,
				await rowsApi.getRowsSummary(base),
				`${base}/rows/summary`,
			),
		enabled,
		/* "Not processed yet" is an answer, not a fault worth retrying. */
		retry: false,
	});
}

export function useFileRowsQuery(
	base: string,
	query: FileRowsQuery,
	enabled: boolean,
) {
	return useQuery({
		queryKey: keys.fileRows.page(base, JSON.stringify(query)),
		queryFn: async () =>
			parsed(
				fileRowsPageSchema,
				await rowsApi.getRowsPage(base, query),
				`${base}/rows`,
			),
		enabled,
		/* Paging, sorting and filtering keep the last page on screen until the next
       one lands, so the table never flashes empty. */
		placeholderData: keepPreviousData,
	});
}

export function useFileRowValuesQuery(
	base: string,
	query: FileRowFilters & { column: string; limit?: number },
	enabled: boolean,
) {
	return useQuery({
		queryKey: keys.fileRows.values(base, JSON.stringify(query)),
		queryFn: async () =>
			parsed(
				fileRowValuesSchema,
				await rowsApi.getRowValues(base, query),
				`${base}/rows/values`,
			),
		enabled,
		placeholderData: keepPreviousData,
	});
}

/** The server writes the workbook; the browser only follows the signed link. */
export function useExportFileRowsMutation(base: string) {
	return useMutation({
		mutationFn: async (
			body: FileRowFilters & { sort?: string; dir?: 'asc' | 'desc' },
		) => {
			const ticket = parsed(
				fileRowsExportSchema,
				await rowsApi.exportRows(base, body),
				`${base}/rows/export`,
			);
			followUrl(ticket.url, ticket.filename);
			return ticket;
		},
	});
}
